import { useRef, type KeyboardEvent, type ReactNode } from 'react';
import { cn } from '../../lib/utils';

export interface SegmentedControlOption<T extends string = string> {
  value: T;
  /** Toujours fourni, meme avec une icone : sert de nom accessible (lecteur d'ecran) et d'infobulle. */
  label: string;
  /** Icone optionnelle. Des qu'une option du groupe en a une, tout le groupe bascule en mode icone seule (le libelle devient un texte visuellement masque). */
  icon?: ReactNode;
}

export interface SegmentedControlProps<T extends string = string> {
  value: T;
  onChange: (value: T) => void;
  options: SegmentedControlOption<T>[];
  disabled?: boolean;
  /** Nom accessible du groupe (ex: "Alignement du texte") — obligatoire, independant des labels d'options. */
  ariaLabel: string;
  className?: string;
}

/**
 * Groupe de boutons exclusifs compact, a la place d'un `Select` a 2-4 choix
 * qui mangerait une ligne entiere pour un resultat equivalent (cf panneaux de
 * proprietes de l'editeur BDisplay, inspires des inspecteurs Figma).
 *
 * Semantique ARIA `radiogroup`/`radio` + navigation clavier en tabindex
 * baladeur (roving tabindex) : Tab entre/sort du groupe en un seul arret,
 * les fleches deplacent la selection ET le focus a l'interieur (comme un
 * groupe de boutons radio natif), Home/End vont aux extremites. L'etat
 * selectionne n'est jamais porte par la seule couleur : fond blanc + ombre
 * legere, et `aria-checked` pour les technologies d'assistance.
 */
export function SegmentedControl<T extends string = string>({
  value,
  onChange,
  options,
  disabled = false,
  ariaLabel,
  className,
}: SegmentedControlProps<T>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const hasIcons = options.some((option) => option.icon);
  const selectedIndex = options.findIndex((option) => option.value === value);

  const focusAndSelect = (index: number) => {
    if (options.length === 0) return;
    const clamped = ((index % options.length) + options.length) % options.length;
    const option = options[clamped];
    onChange(option.value);
    const buttons = containerRef.current?.querySelectorAll<HTMLButtonElement>('[role="radio"]');
    buttons?.[clamped]?.focus();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (disabled) return;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        focusAndSelect(index + 1);
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        focusAndSelect(index - 1);
        break;
      case 'Home':
        event.preventDefault();
        focusAndSelect(0);
        break;
      case 'End':
        event.preventDefault();
        focusAndSelect(options.length - 1);
        break;
      default:
        break;
    }
  };

  return (
    <div
      ref={containerRef}
      role="radiogroup"
      aria-label={ariaLabel}
      className={cn(
        'inline-flex items-stretch gap-0.5 rounded-lg border border-neutral-300 bg-neutral-100 p-0.5',
        disabled && 'opacity-50',
        className
      )}
    >
      {options.map((option, index) => {
        const isSelected = option.value === value;
        const isTabbable = selectedIndex >= 0 ? isSelected : index === 0;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={isSelected}
            title={option.label}
            tabIndex={isTabbable ? 0 : -1}
            disabled={disabled}
            onClick={() => onChange(option.value)}
            onKeyDown={(event) => handleKeyDown(event, index)}
            className={cn(
              'flex h-7 min-w-0 items-center justify-center rounded-md text-xs font-medium transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
              'disabled:cursor-not-allowed',
              hasIcons ? 'w-7 shrink-0' : 'flex-1 px-2',
              isSelected ? 'bg-white text-neutral-900 shadow-sm' : 'text-neutral-500 hover:text-neutral-700'
            )}
          >
            {option.icon ? (
              <>
                <span aria-hidden="true">{option.icon}</span>
                <span className="sr-only">{option.label}</span>
              </>
            ) : (
              // `truncate` : filet de securite si un appelant passe un
              // libelle trop long pour la largeur disponible — mieux vaut un
              // texte coupe proprement (avec l'infobulle `title` en repli)
              // qu'un retour a la ligne qui casserait la hauteur fixe `h-7`.
              <span className="truncate">{option.label}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

SegmentedControl.displayName = 'SegmentedControl';
